import React, { useState, useRef, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getYoutubeVideoId, isYoutubeShort } from '../../utils/youtube';
import { useTestimonials } from '../../context/TestimonialContext';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiStar, FiYoutube, FiMessageSquare, FiZap, FiEdit2, FiTrash2, FiUpload, FiUser, FiImage, FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { stripWrappingQuotes, OPEN_QUOTE, CLOSE_QUOTE } from '../../utils/text';

const PAGE_SIZE = 25;

const PLATFORM_FILTERS = ['Google', 'Youtube', 'Trustpilot', 'Clutch', 'Custom'];

const statusColors = {
  'under review': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'approved': 'bg-green-500/10 text-green-400 border-green-500/20',
  'verified': 'bg-green-500/10 text-green-400 border-green-500/20',
  'banned': 'bg-red-500/10 text-red-400 border-red-500/20'
};

const ReviewCard = ({ review, onEdit, onDelete, onTogglePublish, hiddenPublicly }) => {
  const isVideo = review.source?.toLowerCase() === 'youtube' || !!review.youtubeLink;
  const videoId = isVideo ? getYoutubeVideoId(review.youtubeLink) : null;
  const isShort = isVideo ? isYoutubeShort(review.youtubeLink) : false;
  const isPureVideo = isVideo && (!review.content || review.content.trim() === '');

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`flex flex-col rounded-2xl bg-zinc-900/40 backdrop-blur-md border hover:shadow-[0_4px_30px_rgba(234,88,12,0.05)] transition-all duration-300 overflow-hidden group h-full ${review.isPublished ? 'border-orange-500/10 hover:border-orange-500/30' : 'border-white/5 opacity-70 hover:opacity-100'}`}
    >
      {/* Top Media / Content */}
      {videoId && (
        <div className="w-full flex-shrink-0 border-b border-white/5 relative z-0">
          <LiteYouTubeEmbed
            id={videoId}
            title="YouTube video testimonial"
            wrapperClass={`yt-lite ${isShort ? 'yt-short' : ''}`}
            noCookie={true}
            params="rel=0&modestbranding=1"
          />
          <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md rounded-md text-xs font-medium text-zinc-300 border border-white/10 flex items-center gap-1.5 pointer-events-none z-10">
            <SafeIcon icon={FiYoutube} className="text-orange-500" /> YouTube
          </div>
          <div className="absolute top-3 left-3 z-10 pointer-events-none">
            <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border backdrop-blur-md ${statusColors[review.status || 'under review'] || statusColors['under review']}`}>
              {review.status || 'under review'}
            </span>
          </div>
        </div>
      )}

      {!isPureVideo && (
        <div className="p-6 pb-2 flex-grow relative">
          {!videoId && (
            <div className="absolute top-4 right-4">
              <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${statusColors[review.status || 'under review'] || statusColors['under review']}`}>
                {review.status || 'under review'}
              </span>
            </div>
          )}
          <div className="flex justify-between items-start mb-4 mt-2">
            <div className="flex gap-1">
              {[...Array(review.rating || 5)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="text-yellow-500 fill-yellow-500 text-sm" />
              ))}
            </div>
          </div>
          <p className="text-zinc-300 leading-relaxed min-h-[80px]">{OPEN_QUOTE}{stripWrappingQuotes(review.content)}{CLOSE_QUOTE}</p>
        </div>
      )}

      {/* Author Footer */}
      <div className="p-6 pt-4 mt-auto border-t border-white/5 flex items-center gap-3 bg-zinc-950/30">
        <div className="flex items-center -space-x-2 flex-shrink-0">
          {review.clientAvatarUrl ? (
            <div className="w-10 h-10 rounded-full border-2 border-zinc-900 bg-zinc-800 relative z-20 overflow-hidden shadow-sm">
              <img src={review.clientAvatarUrl} alt={review.authorName} className="w-full h-full object-cover" />
            </div>
          ) : (!review.companyLogo && (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-2 border-zinc-900 flex items-center justify-center relative z-20 shadow-sm">
              <span className="text-amber-100 font-semibold">{review.authorName?.charAt(0) || 'A'}</span>
            </div>
          ))}

          {review.companyLogo && (
            <div className="w-10 h-10 rounded-full border-2 border-zinc-900 bg-white relative z-10 overflow-hidden shadow-sm">
              <img src={review.companyLogo} alt={review.companyName} className="w-full h-full object-contain p-0.5" />
            </div>
          )}
        </div>

        <div className="overflow-hidden flex-1 pl-1">
          <div className="text-amber-50 font-medium text-sm truncate">
            {review.authorName || review.companyName || 'Anonymous'}
          </div>
          <div className="text-zinc-500 text-xs truncate flex items-center gap-1.5">
            {review.authorRole && <span>{review.authorRole}</span>}
            {review.authorRole && review.companyName && <span>•</span>}
            {review.companyName && <span className="font-medium text-zinc-400">{review.companyName}</span>}
          </div>
        </div>

        <div className="px-2 py-1 bg-zinc-800/80 border border-zinc-700 rounded-md text-[10px] font-medium text-zinc-400 flex items-center gap-1.5 flex-shrink-0">
          <SafeIcon icon={FiMessageSquare} className="text-zinc-500" /> {review.source}
        </div>
      </div>

      {/* Actions Footer */}
      <div className="px-5 py-3 border-t border-white/5 bg-[#0a0500] flex items-center justify-between">
        <label className="flex items-center cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              className="sr-only"
              checked={review.isPublished}
              onChange={() => onTogglePublish(review)}
            />
            <div className={`block w-8 h-4 rounded-full transition-colors ${review.isPublished ? 'bg-orange-500' : 'bg-zinc-700 group-hover:bg-zinc-600'}`}></div>
            <div className={`absolute left-1 top-1 bg-white w-2 h-2 rounded-full transition-transform ${review.isPublished ? 'transform translate-x-4' : ''}`}></div>
          </div>
          <span className={`ml-2 text-xs font-medium transition-colors ${review.isPublished ? 'text-amber-100' : 'text-zinc-500 group-hover:text-zinc-400'}`}>
            {review.isPublished ? 'Published' : 'Draft'}
          </span>
        </label>

        {review.isPublished && hiddenPublicly && (
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800/80 text-zinc-400 border border-zinc-700 whitespace-nowrap"
            title="The Free plan shows your newest 25 published reviews publicly. Unpublish newer ones to make room, or upgrade to show all."
          >
            Not shown publicly
          </span>
        )}

        {review.source === 'Custom' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(review)}
              className="p-1.5 text-zinc-500 hover:text-orange-400 transition-colors bg-zinc-900 rounded-md border border-white/5"
              title="Edit"
            >
              <SafeIcon icon={FiEdit2} className="text-xs" />
            </button>
            <button
              onClick={() => onDelete(review.id)}
              className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors bg-zinc-900 rounded-md border border-white/5"
              title="Delete"
            >
              <SafeIcon icon={FiTrash2} className="text-xs" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Reviews = () => {
  const { user } = useAuth();
  const { testimonials, fetchTestimonials, loading } = useTestimonials();
  
  const [activeFilter, setActiveFilter] = useState('All Reviews');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingMedia, setIsFetchingMedia] = useState(false);
  const avatarInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Edit State
  const [editingReviewId, setEditingReviewId] = useState(null);
  
  const initialFormState = {
    authorName: '',
    authorRole: '',
    linkedinUrl: '',
    xUrl: '',
    companyName: '',
    companyWebsite: '',
    reviewUrl: '',
    content: '',
    clientAvatarUrl: '',
    companyLogo: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  // Evaluated consistently to avoid use-before-define linting errors
  const canFetchMedia = formData.companyWebsite?.trim() !== '';

  // File Upload State
  const [avatarFile, setAvatarFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const filters = ['All Reviews', 'Published', 'Draft'];

  const filteredTestimonials = useMemo(() => {
    return testimonials.filter(t => {
      if (activeFilter === 'All Reviews') return true;
      if (activeFilter === 'Published') return t.isPublished;
      if (activeFilter === 'Draft') return !t.isPublished;
      if (!t.source) return false;
      return t.source.toLowerCase() === activeFilter.toLowerCase();
    });
  }, [testimonials, activeFilter]);

  // Free tier: the public page serves only the newest 25 published reviews
  // (by real publish date, matching the RPC's window). Mark the overflow so
  // owners can see which published reviews aren't actually visible.
  const publiclyHiddenIds = useMemo(() => {
    if (user?.tier !== 'free') return new Set();
    const published = testimonials.filter(t => t.isPublished);
    if (published.length <= 25) return new Set();
    const newest25 = [...published]
      .sort((a, b) => new Date(b.effectiveDate || 0) - new Date(a.effectiveDate || 0))
      .slice(0, 25)
      .map(t => t.id);
    const visible = new Set(newest25);
    return new Set(published.filter(t => !visible.has(t.id)).map(t => t.id));
  }, [testimonials, user?.tier]);

  const totalPages = Math.max(1, Math.ceil(filteredTestimonials.length / PAGE_SIZE));

  // Keep the page in range when the filtered set shrinks (filter change, delete).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedTestimonials = filteredTestimonials.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleAutoFetchMedia = async () => {
    if (!canFetchMedia) return;
    setIsFetchingMedia(true);

    // Clean website explicitly for N8N payload
    let cleanWebsite = formData.companyWebsite?.trim() || '';
    cleanWebsite = cleanWebsite.replace(/^https?:\/\//i, '').split('/')[0];

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('You must be logged in to fetch media.');
      }

      const { data, error } = await supabase.functions.invoke('find-review-media', {
        body: {
          website: cleanWebsite,
          name: formData.authorName,
          company_name: formData.companyName
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;
      
      if (data?.error) {
        alert(data.error);
      } else if (data?.logo_url || data?.avatar_url) {
        setFormData(prev => ({
          ...prev,
          companyLogo: data.logo_url || prev.companyLogo,
          clientAvatarUrl: data.avatar_url || prev.clientAvatarUrl
        }));
        
        // Clear manual file selection to show the newly fetched URLs
        if (data.logo_url) setLogoFile(null);
        if (data.avatar_url) setAvatarFile(null);
      } else {
        alert('Could not find media for this website/person.');
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error fetching media. Ensure the N8N webhook is correctly configured.');
    } finally {
      setIsFetchingMedia(false);
    }
  };

  const openNewModal = () => {
    setEditingReviewId(null);
    setFormData(initialFormState);
    setAvatarFile(null);
    setLogoFile(null);
    setIsModalOpen(true);
  };

  const openEditModal = (review) => {
    setEditingReviewId(review.id);
    setFormData({
      authorName: review.authorName || '',
      authorRole: review.authorRole || '',
      linkedinUrl: review.linkedinUrl || '',
      xUrl: review.xUrl || '',
      companyName: review.companyName || '',
      companyWebsite: review.companyWebsite || '',
      reviewUrl: review.reviewUrl || '',
      content: review.content || '',
      clientAvatarUrl: review.clientAvatarUrl || '',
      companyLogo: review.companyLogo || ''
    });
    setAvatarFile(null);
    setLogoFile(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this review?')) return;
    try {
      const { error } = await supabase.from('reviews').delete().eq('id', id);
      if (error) throw error;
      await fetchTestimonials();
    } catch (err) {
      console.error(err);
      alert('Failed to delete review.');
    }
  };

  const handleTogglePublish = async (review) => {
    // Client side limit check
    if (!review.isPublished && !user.is_pro) {
      const publishedCount = testimonials.filter(t => t.isPublished).length;
      if (publishedCount >= 25) {
        alert('Free plans are limited to 25 published reviews. Please upgrade to Pro or unpublish another review first.');
        return;
      }
    }

    try {
      const { error } = await supabase.from('reviews')
        .update({ is_published: !review.isPublished })
        .eq('id', review.id);
      
      if (error) throw error;
      await fetchTestimonials();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to update publish status.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);

    // Normalize and Validate Company Website (Domain Check)
    let cleanWebsite = formData.companyWebsite?.trim() || '';
    if (cleanWebsite) {
      // Strip http://, https://, www. and any paths
      cleanWebsite = cleanWebsite.replace(/^https?:\/\//i, '').split('/')[0];
      const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      if (!domainRegex.test(cleanWebsite)) {
        alert('Please enter a valid company website domain (e.g., acme.com).');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      let finalAvatarUrl = formData.clientAvatarUrl;
      let finalLogoUrl = formData.companyLogo;
      
      // Generate a temporary ID for scoping manual uploads
      const reviewTempId = editingReviewId || (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now().toString());

      // Handle Avatar Upload
      if (avatarFile) {
        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `avatar-${Date.now()}.${fileExt}`;
        const filePath = `manual/${user.id}/${reviewTempId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('n8n-client-avatars')
          .upload(filePath, avatarFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('n8n-client-avatars')
          .getPublicUrl(filePath);

        finalAvatarUrl = publicUrl;
      }

      // Handle Logo Upload
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `logo-${Date.now()}.${fileExt}`;
        const filePath = `manual/${user.id}/${reviewTempId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('n8n-company-logos')
          .upload(filePath, logoFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('n8n-company-logos')
          .getPublicUrl(filePath);

        finalLogoUrl = publicUrl;
      }

      // Map UI form keys to corresponding Supabase DB Columns
      const reviewData = {
        user_id: user.id,
        source: 'Custom',
        client_name: formData.authorName,
        job_role: formData.authorRole,
        company_name: formData.companyName,
        company_website: cleanWebsite, // Save stripped domain
        linkedin_url: formData.linkedinUrl,
        x_url: formData.xUrl,
        review_proof_url: formData.reviewUrl,
        client_avatar_url: finalAvatarUrl,
        company_logo_url: finalLogoUrl,
        content: formData.content,
        rating: 5,
        status: 'under review' // always under review after edit per instructions
      };

      if (editingReviewId) {
        const { error } = await supabase
          .from('reviews')
          .update(reviewData)
          .eq('id', editingReviewId);
        
        if (error) throw error;
      } else {
        // Enforce limit on new insert
        if (!user.is_pro) {
          const publishedCount = testimonials.filter(t => t.isPublished).length;
          if (publishedCount >= 25) {
            reviewData.is_published = false; // insert as unpublished instead of failing
            alert("This review was saved as Draft because you've reached the 25 published limit on the free plan.");
          } else {
            reviewData.is_published = true;
          }
        } else {
          reviewData.is_published = true;
        }

        const { error } = await supabase
          .from('reviews')
          .insert([reviewData]);
          
        if (error) throw error;
      }

      await fetchTestimonials();
      setIsModalOpen(false);
      setFormData(initialFormState);
      setAvatarFile(null);
      setLogoFile(null);

    } catch (err) {
      console.error('Error saving review:', err);
      alert(err.message || 'Failed to save review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyles = "w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl px-4 py-3 text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none";

  const editingReview = editingReviewId ? testimonials.find(t => t.id === editingReviewId) : null;
  const showStatusNote = editingReviewId && editingReview?.status !== 'under review';

  return (
    <div className="max-w-7xl mx-auto pb-12">
      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
          {filters.map(filter => (
            <button
              key={filter}
              onClick={() => { setActiveFilter(filter); setCurrentPage(1); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeFilter === filter
                  ? 'bg-orange-500/20 text-amber-100 border border-orange-500/50 shadow-[0_0_15px_rgba(234,88,12,0.15)]'
                  : 'bg-zinc-900/50 text-zinc-400 border border-transparent hover:bg-zinc-800 hover:text-zinc-200'
              }`}
            >
              {filter}
            </button>
          ))}
          <div className="relative flex-shrink-0">
            <select
              value={PLATFORM_FILTERS.includes(activeFilter) ? activeFilter : ''}
              onChange={(e) => {
                if (e.target.value) { setActiveFilter(e.target.value); setCurrentPage(1); }
              }}
              aria-label="Filter by platform"
              className={`pl-4 pr-9 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap outline-none cursor-pointer appearance-none ${
                PLATFORM_FILTERS.includes(activeFilter)
                  ? 'bg-orange-500/20 text-amber-100 border border-orange-500/50 shadow-[0_0_15px_rgba(234,88,12,0.15)]'
                  : 'bg-zinc-900/50 text-zinc-400 border border-transparent hover:bg-zinc-800 hover:text-zinc-200'
              }`}
            >
              <option value="" disabled className="bg-zinc-900">Platform</option>
              {PLATFORM_FILTERS.map(p => (
                <option key={p} value={p} className="bg-zinc-900">{p}</option>
              ))}
            </select>
            <SafeIcon icon={FiChevronDown} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-current opacity-70 text-sm" />
          </div>
        </div>
        <Button variant="primary" onClick={openNewModal} className="!px-5 !py-2 text-sm flex-shrink-0">
          <SafeIcon icon={FiPlus} /> New Review
        </Button>
      </div>

      {!user?.is_pro && (
        <div className="mb-6 px-4 py-3 bg-zinc-900/50 border border-orange-500/20 rounded-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center">
              <SafeIcon icon={FiStar} className="text-orange-400"/>
            </div>
            <div>
              {publiclyHiddenIds.size > 0 ? (
                <>
                  <div className="text-sm font-medium text-amber-50">Your newest 25 published reviews are shown publicly</div>
                  <div className="text-xs text-zinc-400">{publiclyHiddenIds.size} more published review{publiclyHiddenIds.size === 1 ? ' is' : 's are'} hidden on the Free plan. Upgrade to Pro to show them all.</div>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium text-amber-50">Free Plan Limit: 25 Published Reviews</div>
                  <div className="text-xs text-zinc-400">You have published {testimonials.filter(t => t.isPublished).length} of 25 allowed reviews.</div>
                </>
              )}
            </div>
          </div>
          <Button variant="secondary" className="!py-1.5 !px-3 text-xs border-orange-500/30 text-orange-400 hover:bg-orange-500/10">Upgrade</Button>
        </div>
      )}

      {/* Grid / Empty State */}
      {loading ? (
        <div className="py-20 flex justify-center w-full col-span-full">
          <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
        </div>
      ) : filteredTestimonials.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pagedTestimonials.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                onEdit={openEditModal}
                onDelete={handleDelete}
                onTogglePublish={handleTogglePublish}
                hiddenPublicly={publiclyHiddenIds.has(review.id)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2.5 rounded-xl border border-orange-500/20 bg-zinc-900/50 text-zinc-300 hover:bg-orange-500/10 hover:text-orange-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-900/50 disabled:hover:text-zinc-300"
                aria-label="Previous page"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <span className="text-sm text-zinc-400 tabular-nums">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2.5 rounded-xl border border-orange-500/20 bg-zinc-900/50 text-zinc-300 hover:bg-orange-500/10 hover:text-orange-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-900/50 disabled:hover:text-zinc-300"
                aria-label="Next page"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="py-20 text-center flex flex-col items-center justify-center bg-zinc-900/20 rounded-3xl border border-dashed border-zinc-800">
          <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4 border border-zinc-800">
            <SafeIcon icon={FiMessageSquare} className="text-2xl text-zinc-600" />
          </div>
          <h3 className="text-lg font-medium text-amber-50 mb-1">
            {activeFilter === 'All Reviews' ? 'No reviews found' : `No ${activeFilter} reviews found`}
          </h3>
          <p className="text-zinc-500">Try adjusting your filters or add a new manual review.</p>
        </div>
      )}

      {/* Review Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingReviewId ? "Edit Review" : "Add Manual Review"}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {showStatusNote && (
            <div className="p-3 mb-4 rounded-xl bg-orange-500/10 border border-orange-500/20 text-sm text-orange-200">
              Note: Updating this review will change its status to <strong>"Under Review"</strong>.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Client Name *</label>
              <input
                required
                type="text"
                placeholder="e.g. Jane Doe"
                className={inputStyles}
                value={formData.authorName}
                onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Job Role *</label>
              <input
                required
                type="text"
                placeholder="e.g. CEO"
                className={inputStyles}
                value={formData.authorRole}
                onChange={(e) => setFormData({ ...formData, authorRole: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Company Name *</label>
              <input
                required
                type="text"
                placeholder="e.g. Acme Corp"
                className={inputStyles}
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Company Website *</label>
              <input
                required
                type="text"
                placeholder="e.g. acme.com"
                className={inputStyles}
                value={formData.companyWebsite}
                onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">LinkedIn Profile URL</label>
              <input
                type="url"
                placeholder="https://linkedin.com/in/..."
                className={inputStyles}
                value={formData.linkedinUrl}
                onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">X (Twitter) Profile URL</label>
              <input
                type="url"
                placeholder="https://x.com/..."
                className={inputStyles}
                value={formData.xUrl}
                onChange={(e) => setFormData({ ...formData, xUrl: e.target.value })}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1.5">Review URL (Proof)</label>
              <input
                type="url"
                placeholder="https://..."
                className={inputStyles}
                value={formData.reviewUrl}
                onChange={(e) => setFormData({ ...formData, reviewUrl: e.target.value })}
              />
            </div>

            {/* Visual Uploaders */}
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Client Avatar</label>
                <div className="flex gap-3 items-center">
                  <div className="w-12 h-12 rounded-xl bg-[#0a0500]/80 border border-orange-500/20 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {(avatarFile || formData.clientAvatarUrl) ? (
                      <img 
                        src={avatarFile ? URL.createObjectURL(avatarFile) : formData.clientAvatarUrl} 
                        alt="Avatar" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <SafeIcon icon={FiUser} className="text-zinc-500 text-lg" />
                    )}
                  </div>
                  <div className="flex-1 flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={avatarInputRef}
                      onChange={(e) => setAvatarFile(e.target.files[0])}
                    />
                    <Button type="button" variant="secondary" className="flex-1 !py-2.5 !px-3 text-xs sm:text-sm" onClick={() => avatarInputRef.current?.click()}>
                      <SafeIcon icon={FiUpload} className="text-zinc-400" /> Upload
                    </Button>
                    <Button 
                      type="button" 
                      variant="secondary" 
                      className="!py-2.5 !px-3" 
                      onClick={handleAutoFetchMedia}
                      disabled={isFetchingMedia || !canFetchMedia}
                      title={!canFetchMedia ? "Fill in Company Website to auto-fetch media" : "Auto-fetch media"}
                    >
                      <SafeIcon icon={FiZap} className={isFetchingMedia ? "animate-pulse text-orange-400" : "text-orange-400"} />
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Company Logo</label>
                <div className="flex gap-3 items-center">
                  <div className="w-12 h-12 rounded-xl bg-white border border-orange-500/20 overflow-hidden flex items-center justify-center flex-shrink-0 p-1.5">
                    {(logoFile || formData.companyLogo) ? (
                      <img 
                        src={logoFile ? URL.createObjectURL(logoFile) : formData.companyLogo} 
                        alt="Logo" 
                        className="w-full h-full object-contain" 
                      />
                    ) : (
                      <SafeIcon icon={FiImage} className="text-zinc-400 text-lg" />
                    )}
                  </div>
                  <div className="flex-1 flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={logoInputRef}
                      onChange={(e) => setLogoFile(e.target.files[0])}
                    />
                    <Button type="button" variant="secondary" className="flex-1 !py-2.5 !px-3 text-xs sm:text-sm" onClick={() => logoInputRef.current?.click()}>
                      <SafeIcon icon={FiUpload} className="text-zinc-400" /> Upload
                    </Button>
                    <Button 
                      type="button" 
                      variant="secondary" 
                      className="!py-2.5 !px-3" 
                      onClick={handleAutoFetchMedia}
                      disabled={isFetchingMedia || !canFetchMedia}
                      title={!canFetchMedia ? "Fill in Company Website to auto-fetch media" : "Auto-fetch media"}
                    >
                      <SafeIcon icon={FiZap} className={isFetchingMedia ? "animate-pulse text-orange-400" : "text-orange-400"} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-zinc-400 mb-1.5">Testimonial *</label>
            <textarea
              required
              placeholder="What did they say about your product?"
              rows={4}
              className={`${inputStyles} resize-none`}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            />
          </div>

          <div className="pt-2 flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Review'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Reviews;